import { IconButton } from '@components/iconButton/IconButton';
import {
  Carousel,
  type CarouselVariant,
} from '@shared/components/carousel/Carousel';
import { type RestaurantEventImage } from '@shared/types/restaurants';
import { getRestaurantImagePath } from '@shared/utils/getRestaurantImagePath';
import { noop } from 'lodash-es';
import React from 'react';
import styles from './ImageGalleryPreview.scss';

type ImageData = Pick<RestaurantEventImage, 'imageKey'>;

interface ImageGalleryPreviewProps {
  eventName: string;
  images: ImageData[];
  /**
   * Caps how many `images` are used. When not set, only the first image is shown in the static
   * preview; when `enableImageCarousel` is true and `isEditing` is false, the full list is used for
   * the carousel.
   */
  maxImages?: number;
  isEditing?: boolean;
  onRemoveImage?: (imageKey: string) => void;
  /**
   * When true, multiple images are shown in a carousel (full-width variant + nav).
   * Ignored while `isEditing` is true so the remove control stays on a single image.
   */
  enableImageCarousel?: boolean;
  /** Omit for default `fullWidth`. `compact` uses intrinsic sizing (no parent height). */
  carouselVariant?: CarouselVariant;
  /**
   * Only when `carouselVariant` isn't `compact`. `fitContent` (default) lets height follow the
   * active image at full width, capped by `--carousel-fit-content-slide-max-height`. `cover` always
   * fills 100% width and a fixed height (`--carousel-cover-height`, default 450px), cropping the
   * image instead of letterboxing it.
   */
  fullWidthHeightMode?: 'fitContent' | 'cover';
}

export const ImageGalleryPreview: React.FC<ImageGalleryPreviewProps> = ({
  eventName,
  images = [],
  maxImages,
  isEditing = false,
  onRemoveImage = () => noop,
  enableImageCarousel = false,
  carouselVariant,
  fullWidthHeightMode = 'fitContent',
}) => {
  const imageCap =
    maxImages ?? (enableImageCarousel && !isEditing ? images.length : 1);
  const displayImages = images.slice(0, imageCap);
  const firstImage = displayImages[0];
  const showCarousel =
    enableImageCarousel && !isEditing && displayImages.length > 0;

  return (
    <div className={styles.container}>
      {showCarousel ? (
        <section
          className={styles.carouselWrapper}
          aria-label={`${eventName} photo gallery`}
        >
          <Carousel
            key={displayImages.map((img) => img.imageKey).join('|')}
            className={styles.carouselInContainer}
            {...(carouselVariant === 'compact'
              ? {
                  variant: 'compact' as const,
                  compactSizing: 'intrinsic' as const,
                }
              : {})}
            fullWidthImageAlt={eventName}
            slides={displayImages.map((image) => {
              const src = getRestaurantImagePath(image.imageKey);
              return {
                key: image.imageKey,
                src,
                backgroundImage: `url(${src})`,
              };
            })}
            {...(carouselVariant === 'compact' ? {} : { fullWidthHeightMode })}
          />
        </section>
      ) : null}
      {!showCarousel && firstImage ? (
        <div className={styles.imageWrapper}>
          {isEditing && (
            <IconButton
              ariaLabel="Remove image"
              className={styles.removeImageButton}
              iconClassName={styles.removeImageIcon}
              iconName="close"
              onClick={() => onRemoveImage(firstImage.imageKey)}
            />
          )}
          <img
            src={getRestaurantImagePath(firstImage.imageKey)}
            alt={eventName}
          />
        </div>
      ) : null}
    </div>
  );
};
